import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { filter } from 'rxjs/operators';
import { Observable, Subscription } from 'rxjs';
import { isEqual } from 'lodash';

import { SharedCommonUtility } from '../../../../../../shared/utils/common.utility';
import { CommonControlsViewMode } from '../../../../constants/common-controls-view-mode';
import { CommonUtility } from '../../../../utility/common.utility';
import { TranslateService } from '../../../../translate/translate.service';
import { FeatureFlagService } from '../../../../services/feature-flag/feature-flag.service';
import { FeatureFlagCollection } from '../../../../../../shared/interfaces/feature-flag.interface';

export interface UsageLimitFieldContext {
  id?: string;
  customMessage?: string;
  label: string;
  field: string;
  fullWidthInput?: boolean;
  readonly?: boolean;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  overrideErrors?: Record<string, string>;
  tooltip?: string;
  noLabel?: boolean;
  hiddenLabel?: boolean;
  width?: string;
  maxLimit?: number;
}

@Component({
  selector: 'app-usage-limit',
  templateUrl: './usage-limit.component.html',
  styleUrls: ['./usage-limit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class UsageLimitComponent implements OnChanges, OnInit, OnDestroy {
  @Input()
  public form: UntypedFormGroup;
  @Input()
  public context: UsageLimitFieldContext;
  @Input()
  public formValidationRequest$: Observable<void>;
  @Input()
  public view: CommonControlsViewMode;
  @Input()
  public defaultValue: number = null;

  public subscription: Subscription = new Subscription();
  public labelId: string = CommonUtility.createUniqueDOMId();
  public subLabelId: string = CommonUtility.createUniqueDOMId();
  private value: number = null;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private translateService: TranslateService,
    private featureFlagService: FeatureFlagService,
  ) {
    this.view = 'col-view';
  }

  get required(): boolean {
    return typeof this.context.required === 'boolean' ? this.context.required : true;
  }

  get readonly(): boolean {
    return this.context.readonly === true;
  }

  get fullWidthInput(): boolean {
    if (SharedCommonUtility.isNullish(this.context.fullWidthInput) && SharedCommonUtility.isNullish(this.context.width)) {
      return true;
    }
    return this.context.fullWidthInput;
  }

  get displayValue(): number {
    return this.value;
  }

  set displayValue(v: number) {
    this.value = v;
    if (this.form && this.context?.field) {
      const control = this.form.get(this.context.field);
      if (control && control.value !== this.value) {
        control.setValue(Number(this.value));
        control.markAsDirty();
        control.markAsTouched();
        this.changeDetectorRef.detectChanges();
      }
    }
  }

  get customLabel(): string {
    const translatedLabel = this.translateService.instant(this.context.label);
    return this.translateService.instant('label_usage_limit_input', [translatedLabel]);
  }

  public ngOnInit(): void {
    this.subscription.add(
      this.featureFlagService
        .variation$(FeatureFlagCollection.preservePackageUsageLimits, false)
        .subscribe((preservePackageUsageLimits: boolean) => {
          if (preservePackageUsageLimits) {
            return;
          }

          if (SharedCommonUtility.isNullish(this.defaultValue)) {
            this.defaultValue = 0;
          }

          if (this.form && this.context?.field) {
            const control = this.form.get(this.context.field);
            if (control) {
              this.displayValue = control.value ?? this.defaultValue;
              this.changeDetectorRef.detectChanges();
            }
          }
        }),
    );

    if (this.formValidationRequest$) {
      this.subscription.add(
        this.formValidationRequest$.subscribe(() => {
          if (this.form && this.context?.field) {
            const field: AbstractControl | null = this.form.get(this.context.field);
            if (field) {
              field.markAsDirty();
              field.markAsTouched();
              this.changeDetectorRef.detectChanges();
            }
          }
        }),
      );
    }

    if (this.form && this.context?.field) {
      const control = this.form.get(this.context.field);
      if (control) {
        this.subscription.add(
          control.valueChanges
            .pipe(
              filter(SharedCommonUtility.notNullish),
              filter((value: number) => value !== this.value),
            )
            .subscribe((value: number) => {
              this.value = value;
              control.markAsDirty();
              control.markAsTouched();
              this.changeDetectorRef.detectChanges();
            }),
        );
      }
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (
      SharedCommonUtility.notNullish(changes.defaultValue) &&
      SharedCommonUtility.notNullishOrEmpty(changes.defaultValue.currentValue) &&
      !isEqual(changes.defaultValue.currentValue, changes.defaultValue.previousValue)
    ) {
      this.value = this.form ? this.form.get(this.context.field).value : this.defaultValue;
    }
  }

  public ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }
}
